import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './GuestDashboard.css';

const GuestDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBookings: 0,
    upcomingBookings: 0,
    pastBookings: 0,
    pendingPayments: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [currentUser, navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // F-12: endpoint now requires auth — send Firebase ID token.
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`/api/guest-bookings/user/${currentUser.uid}`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch bookings');
      
      const data = await response.json();
      const bookings = data.bookings || [];

      // Calculate stats
      const now = new Date();
      const upcoming = bookings.filter(b => 
        new Date(b.check_in_date) > now && b.status !== 'cancelled'
      );
      const past = bookings.filter(b => 
        new Date(b.check_out_date) < now || b.status === 'checked_out'
      );
      const pending = bookings.filter(b => 
        b.payment_status === 'pending' && b.status !== 'cancelled'
      );

      setStats({
        totalBookings: bookings.length,
        upcomingBookings: upcoming.length,
        pastBookings: past.length,
        pendingPayments: pending.length,
      });

      // Get recent bookings (last 5)
      setRecentBookings(bookings.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="guest-dashboard">
        <div className="dashboard-loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="guest-dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {currentUser?.displayName || 'Guest'}</h1>
        <p>Here's an overview of your stays and activity</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Bookings</div>
            <div className="stat-value">{stats.totalBookings}</div>
          </div>
        </div>

        <div className="stat-card upcoming">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <polyline points="12 6 12 12 16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Upcoming</div>
            <div className="stat-value">{stats.upcomingBookings}</div>
          </div>
        </div>

        <div className="stat-card past">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Completed</div>
            <div className="stat-value">{stats.pastBookings}</div>
          </div>
        </div>

        <div className="stat-card pending">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <line x1="12" y1="1" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Pending Payments</div>
            <div className="stat-value">{stats.pendingPayments}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <p className="section-title">Quick Actions</p>
        <div className="actions-grid">
          <button className="action-card" onClick={() => navigate('/booking')}>
            <div className="action-card-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="10" r="3" strokeWidth="2"/>
              </svg>
            </div>
            <div className="action-card-body">
              <h3>Book a Hotel</h3>
              <p>Find and book your next stay</p>
            </div>
          </button>

          <button className="action-card" onClick={() => navigate('/my-bookings')}>
            <div className="action-card-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="14 2 14 8 20 8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="13" x2="8" y2="13" strokeWidth="2" strokeLinecap="round"/>
                <line x1="16" y1="17" x2="8" y2="17" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="action-card-body">
              <h3>My Bookings</h3>
              <p>Manage your reservations</p>
            </div>
          </button>

          <button className="action-card" onClick={() => navigate('/guest-profile')}>
            <div className="action-card-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="action-card-body">
              <h3>Edit Profile</h3>
              <p>Update your personal info</p>
            </div>
          </button>
        </div>
      </div>

      {/* Recent Bookings */}
      {recentBookings.length > 0 && (
        <div className="recent-bookings">
          <div className="section-header">
            <h2>Recent Bookings</h2>
            <button onClick={() => navigate('/my-bookings')} className="view-all-btn">View all</button>
          </div>
          <div className="bookings-list">
            {recentBookings.map(booking => (
              <div key={booking.booking_id} className="booking-item">
                <div className="booking-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="booking-details">
                  <h4>{booking.hotel_name || 'Hotel'}</h4>
                  <p className="booking-dates">
                    {formatDate(booking.check_in_date)} - {formatDate(booking.check_out_date)}
                  </p>
                </div>
                <div className="booking-status">
                  <span className={`status-badge ${booking.status}`}>
                    {booking.status}
                  </span>
                  <span className={`payment-badge ${booking.payment_status}`}>
                    {booking.payment_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {recentBookings.length === 0 && (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3>No bookings yet</h3>
          <p>Start planning your next adventure!</p>
          <button className="btn-primary" onClick={() => navigate('/booking')}>
            Browse Hotels
          </button>
        </div>
      )}
    </div>
  );
};

export default GuestDashboard;
